import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { tournamentAPI, authAPI, paymentsAPI, teamAPI } from '../../../utils/api';
import { AuthContext } from '../../../context/AuthContext';
import Navbar from '../../../components/Navbar';
import RegistrationModal from '../ui/RegistrationModal';
import './TournamentDetail.css';

// Game poster images
// Note: banner/hero images should come from backend (tournament object).
// Fallbacks are handled in getHeroImage below.

/* ─── Map pool data ──────────────────────────────────────── */
// Map names come from the shared util `src/utils/gameMaps.js` so all surfaces
// (CreateTournament, TournamentDetail, etc.) stay in sync. The extra metadata
// (matchCount, boInfo) is tournament-display-specific and stays here.
import { getMapsForGame } from '../../../utils/gameMaps';

const GAME_DISPLAY_INFO = {
  BGMI: { matchCount: 4, boInfo: '' },
  'Free Fire': { matchCount: 3, boInfo: '' },
  Scarfall: { matchCount: 3, boInfo: '' },
  Valorant: { matchCount: 0, boInfo: 'BO3' },
  'COD Mobile': { matchCount: 0, boInfo: 'BO5' },
};

const getGameMaps = (gameName = '') => getMapsForGame(gameName);

const getGameMapInfo = (gameName = '') => {
  const lc = (gameName || '').toLowerCase();
  let key = 'BGMI';
  if (lc.includes('free fire') || lc.includes('freefire')) key = 'Free Fire';
  else if (lc.includes('scar')) key = 'Scarfall';
  else if (lc.includes('valorant')) key = 'Valorant';
  else if (lc.includes('cod')) key = 'COD Mobile';
  return { maps: getMapsForGame(gameName), ...(GAME_DISPLAY_INFO[key] || GAME_DISPLAY_INFO.BGMI) };
};

/* ─── Is 5v5 game ────────────────────────────────────────── */
const is5v5Game = (gameName = '', gameMode = '') => {
  const gn = gameName.toLowerCase();
  const gm = (gameMode || '').toLowerCase();
  return gm === '5v5' || gm.includes('5v5') || gn.includes('valorant') || gn.includes('cod');
};

/* ─── Status helpers ─────────────────────────────────────── */
const getStatusLabel = (status) => {
  if (status === 'ongoing') return 'LIVE';
  if (status === 'completed') return 'COMPLETED';
  return 'REGISTRATION OPEN';
};

const getStatusClass = (status) => {
  if (status === 'ongoing') return 'td-badge td-badge-live';
  if (status === 'completed') return 'td-badge td-badge-completed';
  return 'td-badge td-badge-open';
};

/* ─── SVG Icons (inline, no external lib needed) ─────────── */
const IconArrowLeft = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 18, height: 18 }}
  >
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);
const IconArrowRight = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const IconTrophy = ({ style }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style || { width: 16, height: 16 }}
  >
    <path d="M6 9H4.5a2.5 2.5 0 010-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
    <path d="M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0012 0V2z" />
  </svg>
);
const IconGamepad = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 15, height: 15 }}
  >
    <line x1="6" y1="12" x2="10" y2="12" />
    <line x1="8" y1="10" x2="8" y2="14" />
    <line x1="15" y1="13" x2="15.01" y2="13" />
    <line x1="18" y1="11" x2="18.01" y2="11" />
    <rect x="2" y="6" width="20" height="12" rx="2" />
  </svg>
);
const IconVideo = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 18, height: 18 }}
  >
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const IconExternalLink = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);
const IconShare = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 15, height: 15 }}
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);
const IconMapPin = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const IconTarget = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);
const IconClock = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconShield = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const IconUsers = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);
const IconStar = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ width: 11, height: 11 }}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const IconBadgeCheck = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ width: 15, height: 15 }}>
    <path
      d="M12 1l3.09 6.26L22 8.27l-5 4.87 1.18 6.88L12 16.77l-6.18 3.25L7 12.14 2 8.27l6.91-1.01z"
      fill="#60a5fa"
    />
    <path
      d="M9 12l2 2 4-4"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IconChevronRight = ({ style }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style || { width: 20, height: 20 }}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const IconCalendar = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 15, height: 15 }}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconTicket = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <line x1="9" y1="9" x2="9" y2="15" strokeDasharray="2 2" />
  </svg>
);

/* ═══════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════ */
const TournamentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromManage = location.state?.fromManage;

  /* ─── State ─────────────────────────────────────────────── */
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [matchSchedule, setMatchSchedule] = useState([]);
  const [scheduleByRound, setScheduleByRound] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [expandedSchedule, setExpandedSchedule] = useState(true);
  const [selectedScheduleRound, setSelectedScheduleRound] = useState(null);
  const [selectedScheduleGroup, setSelectedScheduleGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('schedule');
  const [prizeTab, setPrizeTab] = useState('cash');
  const [tournamentResults, setTournamentResults] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showTeamRegistration, setShowTeamRegistration] = useState(false);
  const [userHasTeam, setUserHasTeam] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    team_name: '',
    teammate_emails: [],
    in_game_details: { ign: '', uid: '', rank: '' },
  });
  const [usernameSuggestions, setUsernameSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const suggestionTimeoutRef = useRef({});

  const { isAuthenticated, isPlayer, isHost, user } = useContext(AuthContext);

  /* ─── Fetch tournament ───────────────────────────────────── */
  useEffect(() => {
    const loadTournament = async () => {
      await fetchTournament();
    };
    loadTournament();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (tournament && isHost()) {
      const hostProfileId = user?.profile?.id || user?.host_profile?.id;
      const tournamentHostId = tournament.host?.id;
      if (hostProfileId && tournamentHostId && Number(hostProfileId) === Number(tournamentHostId)) {
        navigate(`/tournaments/${id}/manage`);
      }
    }
  }, [tournament, isHost, user, id, navigate]);

  const fetchTournament = async () => {
    try {
      const response = await tournamentAPI.getTournament(id);
      const data = response.data;
      // Redirect if this is actually a scrim
      if ((data.event_mode || '').toUpperCase() === 'SCRIM') {
        navigate(`/scrims/${id}`, { replace: true });
        return;
      }
      setTournament(data);
      // Fetch results leaderboard for completed tournaments
      if (data.status === 'completed') {
        try {
          const statsRes = await tournamentAPI.getTournamentStats(id);
          setTournamentResults(statsRes.data?.leaderboard || []);
        } catch (e) {
          // non-blocking — results card just won't show
        }
      }
    } catch (error) {
      console.error('Error fetching tournament:', error);
    } finally {
      setLoading(false);
    }
  };

  /* ─── Fetch match schedule ───────────────────────────────── */
  const fetchMatchSchedule = async (tournamentData) => {
    if (!tournamentData?.rounds?.length) return;
    if (!isAuthenticated()) return;
    setScheduleLoading(true);
    try {
      const allMatches = [];
      const byRound = {};
      for (const roundConfig of tournamentData.rounds) {
        try {
          const response = await tournamentAPI.getRoundGroups(tournamentData.id, roundConfig.round);
          const groups = response.data.groups || [];
          if (groups.length > 0) {
            byRound[roundConfig.round] = { groups: [] };
            groups.forEach((group) => {
              const groupMatches = (group.matches || []).map((match) => ({
                ...match,
                group_name: group.group_name,
                round_number: roundConfig.round,
              }));
              byRound[roundConfig.round].groups.push({
                group_name: group.group_name,
                group_id: group.id,
                matches: groupMatches,
              });
              allMatches.push(...groupMatches);
            });
          }
        } catch (err) {
          console.debug(`No groups for round ${roundConfig.round}:`, err.message);
        }
      }
      setMatchSchedule(allMatches);
      setScheduleByRound(byRound);
      // Auto-select first available round
      const roundKeys = Object.keys(byRound)
        .map(Number)
        .sort((a, b) => a - b);
      if (roundKeys.length > 0 && !selectedScheduleRound) {
        setSelectedScheduleRound(roundKeys[0]);
        if (byRound[roundKeys[0]].groups.length > 0) {
          setSelectedScheduleGroup(byRound[roundKeys[0]].groups[0].group_name);
        }
      }
    } catch (error) {
      console.error('Error fetching match schedule:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  useEffect(() => {
    if (tournament?.rounds?.length) fetchMatchSchedule(tournament);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.id]);

  // Auto-refresh match schedule every 30s for live tournaments
  useEffect(() => {
    if (!tournament || tournament.status !== 'ongoing') return;
    const interval = setInterval(() => {
      if (tournament?.rounds?.length) fetchMatchSchedule(tournament);
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.id, tournament?.status]);

  /* ─── Registration helpers ───────────────────────────────── */
  const getRequiredPlayers = (gameMode) => {
    const modeMap = { Squad: 4, Duo: 2, Solo: 1, '5v5': 5, '4v4': 4, '2v2': 2 };
    return modeMap[gameMode] || 1;
  };

  useEffect(() => {
    if (showRegisterModal && tournament) {
      const requiredPlayers = getRequiredPlayers(tournament.game_mode);
      const teammateCount = Math.max(0, requiredPlayers - 1);
      setRegistrationData({
        team_name: '',
        teammate_emails: Array(teammateCount).fill(''), // Show the correct number of fields (all required)
        in_game_details: { ign: '', uid: '', rank: '' },
      });
      setUsernameSuggestions({});
      setShowSuggestions({});
      // Check if user has a permanent (non-temporary) team
      if (isAuthenticated() && isPlayer()) {
        teamAPI
          .getTeams({ mine: 'true' })
          .then((res) => {
            const teams = res.data.results || res.data || [];
            const permanentTeams = teams.filter((t) => !t.is_temporary);
            setUserHasTeam(permanentTeams.length > 0);
          })
          .catch(() => setUserHasTeam(false));
      }
    }
    return () => {
      Object.values(suggestionTimeoutRef.current).forEach((t) => t && clearTimeout(t));
      suggestionTimeoutRef.current = {};
    };
  }, [showRegisterModal, tournament, user]);

  const searchUsernames = async (query, fieldIndex) => {
    if (!query || query.length < 2) {
      setUsernameSuggestions((prev) => ({ ...prev, [fieldIndex]: [] }));
      setShowSuggestions((prev) => ({ ...prev, [fieldIndex]: false }));
      return;
    }
    if (suggestionTimeoutRef.current[fieldIndex])
      clearTimeout(suggestionTimeoutRef.current[fieldIndex]);
    suggestionTimeoutRef.current[fieldIndex] = setTimeout(async () => {
      try {
        const response = await authAPI.searchPlayerUsernames(query);
        const suggestions = response.data.results || [];
        setUsernameSuggestions((prev) => ({ ...prev, [fieldIndex]: suggestions }));
        setShowSuggestions((prev) => ({ ...prev, [fieldIndex]: true }));
      } catch (error) {
        console.error('Error searching usernames:', error);
        setUsernameSuggestions((prev) => ({ ...prev, [fieldIndex]: [] }));
      }
    }, 300);
  };

  const handleTeammateEmailChange = (index, value) => {
    const newEmails = [...registrationData.teammate_emails];
    newEmails[index] = value;
    setRegistrationData({ ...registrationData, teammate_emails: newEmails });
  };

  const addTeammateField = () => {
    if (registrationData.teammate_emails.length < 5) {
      setRegistrationData({
        ...registrationData,
        teammate_emails: [...registrationData.teammate_emails, ''],
      });
    }
  };

  const removeTeammateField = (index) => {
    const newEmails = registrationData.teammate_emails.filter((_, i) => i !== index);
    setRegistrationData({ ...registrationData, teammate_emails: newEmails });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!isAuthenticated() || !isPlayer()) {
      localStorage.setItem('post_verify_redirect', location.pathname);
      navigate('/player/login', { state: { next: location.pathname } });
      return;
    }
    if (!registrationData.team_name.trim()) {
      alert('Please enter a team name');
      return;
    }

    const emails = registrationData.teammate_emails || [];
    const requiredPlayers = getRequiredPlayers(tournament.game_mode);
    const requiredTeammateEmails = Math.max(0, requiredPlayers - 1);

    // Check if all required teammate emails are provided
    if (requiredTeammateEmails > 0) {
      // For 5v5 (Valorant/COD) and Squad modes, all teammate emails are MANDATORY
      const nonEmptyEmails = emails.filter((e) => e && e.trim());
      if (nonEmptyEmails.length < requiredTeammateEmails) {
        alert(
          `This tournament requires ${requiredTeammateEmails} teammate email(s). You provided ${nonEmptyEmails.length}.`
        );
        return;
      }
    }

    // Maximum 5 teammates allowed
    if (emails.length > 5) {
      alert('Maximum 5 teammates allowed');
      return;
    }

    // Only validate emails that are provided (filter out empty ones)
    const nonEmptyEmails = emails.filter((e) => e && e.trim());
    const normalized = nonEmptyEmails.map((e) => e.trim().toLowerCase());

    // Validate emails
    for (let i = 0; i < normalized.length; i++) {
      const eMail = normalized[i];
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(eMail)) {
        alert(`Invalid email at teammate ${i + 1}: ${eMail}`);
        return;
      }
    }

    // Check for duplicates
    const uniqueSet = new Set(normalized);
    if (uniqueSet.size !== normalized.length) {
      alert('Duplicate teammate emails are not allowed');
      return;
    }

    // Check captain email not in list
    const captainEmail = (user?.user?.email || '').toLowerCase();
    if (captainEmail && normalized.includes(captainEmail)) {
      alert('You cannot include your own (captain) email in the teammate list.');
      return;
    }
    try {
      const payload = {
        team_name: registrationData.team_name,
        teammate_emails: normalized, // Send only non-empty, validated emails
      };
      const initResp = await tournamentAPI.registerInitiate(id, payload);
      const registration = initResp.data;
      const isFree = parseFloat(tournament.entry_fee) === 0;
      if (isFree) {
        alert('Registration successful! You are now registered for this tournament.');
        setShowRegisterModal(false);
        fetchTournament();
      } else {
        try {
          const regId =
            registration?.registration_id || registration?.registrationId || registration?.id;
          if (!registration || !regId) {
            alert(
              'Registration created but missing registration id. Please refresh and try again.'
            );
            return;
          }
          const paymentPayload = {
            payment_type: 'entry_fee',
            amount: Number(tournament.entry_fee) || 0,
            registration_id: regId,
            tournament_id: id,
          };
          const payResp = await paymentsAPI.startPayment(paymentPayload);
          const paymentData = payResp.data;
          const redirect =
            paymentData.redirect_url || paymentData.payment_url || paymentData.paymentUrl;
          if (redirect) {
            window.location.href = redirect;
            return;
          }
          alert('Registration initiated. Please complete payment from the next screen.');
          setShowRegisterModal(false);
          fetchTournament();
        } catch (payErr) {
          const errBody = payErr.response?.data;
          const friendly =
            errBody?.registration_id?.[0] ||
            errBody?.error ||
            errBody?.details ||
            'Registration created but payment failed to start.';
          alert(friendly);
        }
      }
    } catch (error) {
      alert(error.response?.data?.error || error.response?.data?.message || 'Registration failed.');
    }
  };

  /* ─── QR download ────────────────────────────────────────── */
  const downloadQr = async () => {
    try {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=360x360&data=${encodeURIComponent(window.location.href)}&bgcolor=ffffff&color=000000&format=png`;
      const resp = await fetch(qrUrl);
      if (!resp.ok) throw new Error('Failed to fetch QR image');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tournament-${id}-qr.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.open(
        `https://api.qrserver.com/v1/create-qr-code/?size=360x360&data=${encodeURIComponent(window.location.href)}&bgcolor=ffffff&color=000000&format=png`,
        '_blank'
      );
    }
  };

  /* ─── Registration button logic ─────────────────────────── */
  const renderRegistrationButton = () => {
    if (isHost()) return null;

    if (isPlayer()) {
      const regStatus = tournament.user_registration_status;
      if (regStatus) {
        const map = {
          pending: {
            text: '⏳ Registration Pending — Complete Payment',
            cls: 'td-reg-status td-reg-status-pending',
          },
          confirmed: {
            text: '✅ You are Registered',
            cls: 'td-reg-status td-reg-status-confirmed',
          },
          cancelled: {
            text: '❌ Registration Cancelled',
            cls: 'td-reg-status td-reg-status-cancelled',
          },
        };
        const info = map[regStatus] || {
          text: `Registered (${regStatus})`,
          cls: 'td-reg-status td-reg-status-closed',
        };
        return <div className={info.cls}>{info.text}</div>;
      }

      if (!tournament.registration_start || !tournament.registration_end) {
        return <div className="td-reg-status td-reg-status-closed">Registration dates not set</div>;
      }

      const now = new Date();
      const regStart = new Date(tournament.registration_start);
      const regEnd = new Date(tournament.registration_end);

      if (isNaN(regStart.getTime()) || isNaN(regEnd.getTime())) {
        return <div className="td-reg-status td-reg-status-closed">Invalid registration dates</div>;
      }

      const buffer = 5 * 60 * 1000;
      const isRegistrationOpen = now >= regStart.getTime() && now <= regEnd.getTime() + buffer;
      const isFull = tournament.current_participants >= tournament.max_participants;
      const canRegister =
        isRegistrationOpen &&
        !isFull &&
        (tournament.status === 'upcoming' || tournament.status === 'registration_open');

      if (canRegister) {
        return (
          <button
            className="td-join-btn"
            onClick={() => {
              if (!isAuthenticated() || !isPlayer()) {
                localStorage.setItem('post_verify_redirect', location.pathname);
                navigate('/player-auth', { state: { next: location.pathname } });
              } else {
                setShowRegisterModal(true);
              }
            }}
          >
            Join Tournament <IconArrowRight />
          </button>
        );
      }

      let message = 'Registration Closed';
      if (!isRegistrationOpen) {
        if (now < regStart) {
          message = `Registration opens ${regStart.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`;
        } else {
          message = `Registration closed on ${regEnd.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`;
        }
      } else if (isFull) {
        message = `Tournament is Full (${tournament.current_participants}/${tournament.max_participants})`;
      } else if (tournament.status !== 'upcoming') {
        message =
          tournament.status === 'ongoing' ? 'Tournament Live' : `Tournament ${tournament.status}`;
      }
      return <div className="td-reg-status td-reg-status-closed">{message}</div>;
    }

    return (
      <button
        className="td-join-btn"
        onClick={() => {
          localStorage.setItem('post_verify_redirect', location.pathname);
          navigate('/player/login', { state: { next: location.pathname } });
        }}
      >
        Login to Register <IconArrowRight />
      </button>
    );
  };

  /* ─── Prize distribution ─────────────────────────────────── */
  const prizePool = parseFloat((tournament?.prize_pool || '0').toString().replace(/,/g, '')) || 0;
  const formatPrize = (amount) => `₹${Math.round(amount).toLocaleString('en-IN')}`;

  /* ─── Loading / not found states ─────────────────────────── */
  if (loading) {
    return (
      <div className="td-loading">
        <div className="td-spinner" />
        <p style={{ color: 'hsl(220 5% 55%)', fontSize: 14 }}>Loading tournament…</p>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="td-loading">
        <p style={{ color: 'hsl(220 5% 55%)', fontSize: 16 }}>Tournament not found.</p>
      </div>
    );
  }

  /* ─── Derived values ─────────────────────────────────────── */
  // Build hero image URL from backend-provided fields (production-ready).
  const mediaBase = process.env.REACT_APP_MEDIA_URL
    ? process.env.REACT_APP_MEDIA_URL.replace(/\/media\/?$/, '').replace(/\/$/, '')
    : '';
  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // Support paths that may start with /media or similar
    if (mediaBase) return `${mediaBase}${url.startsWith('/') ? '' : '/'}${url}`;
    return url;
  };
  const heroImage =
    resolveUrl(tournament?.hero_image) ||
    resolveUrl(tournament?.banner_image) ||
    resolveUrl(tournament?.image) ||
    resolveUrl(tournament?.poster) ||
    null;
  const is5v5 = is5v5Game(tournament.game_name, tournament.game_mode);
  const entryFeeNum = parseFloat(tournament.entry_fee) === 0 ? 'FREE' : `₹${tournament.entry_fee}`;

  const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // Append T00:00:00 for date-only strings so JS parses in local time, not UTC
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? dateStr + 'T00:00:00' : dateStr;
      return new Date(normalized).toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return dateStr;
    }
  };

  /* ─────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Back to Manage button — shown when navigated from ManageTournament */}
      {fromManage && (
        <div style={{ padding: '1rem 1.5rem 0' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.8125rem',
              fontWeight: 500,
              border: '1px solid hsl(var(--border) / 0.7)',
              color: 'hsl(var(--foreground))',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            ← Back to Manage
          </button>
        </div>
      )}
      {/* ── Standard Navbar ── */}
      <Navbar />

      {/* ══════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════ */}
      <section className="td-hero-section" style={{ paddingTop: '64px' }}>
        <div
          className="td-hero-image-wrapper"
          style={{
            backgroundImage: heroImage
              ? undefined
              : 'linear-gradient(180deg, rgba(8,8,10,1) 0%, rgba(18,16,22,1) 100%)',
          }}
        >
          {heroImage ? (
            <img src={heroImage} alt={tournament.title} className="td-hero-image" />
          ) : null}
          <div className="td-hero-gradient-bottom" />
          <div className="td-hero-gradient-sides" />

          {/* Back button */}
          <button
            className="td-back-btn"
            onClick={() => {
              if (!isAuthenticated() || !isPlayer()) {
                // No session (e.g. opened verify link in different browser) — go to auth
                navigate('/player-auth', { state: { next: location.pathname } });
              } else {
                navigate(-1);
              }
            }}
            title="Go back"
          >
            <IconArrowLeft />
          </button>
        </div>

        {/* Hero content overlay */}
        <div className="td-hero-content">
          <div className="td-hero-inner">
            {/* Left: title + meta + actions */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              {/* Status badge */}
              <span className={getStatusClass(tournament.status)}>
                {getStatusLabel(tournament.status)}
              </span>

              {/* Title */}
              <h1 className="td-title">{tournament.title}</h1>

              {/* Info row */}
              <div className="td-info-row">
                <span className="td-info-pill">
                  <IconGamepad className="td-info-pill-icon" />
                  <strong>{tournament.game_name}</strong>
                  <span style={{ color: 'hsl(220 5% 55%)', margin: '0 2px' }}>•</span>
                  <span style={{ color: 'hsl(220 5% 70%)' }}>{tournament.game_mode}</span>
                </span>
                <span className="td-prize-pill">
                  <IconTrophy style={{ width: 16, height: 16 }} />
                  Prize Pool: ₹{tournament.prize_pool}
                </span>
              </div>

              {/* Actions: Join/Register + Share + tags */}
              <div className="td-actions-inner">
                {renderRegistrationButton()}
                <div className="td-action-tags">
                  <button className="td-share-btn" onClick={() => setShowShareModal(true)}>
                    <IconShare /> Share
                  </button>
                  {tournament.map_name && (
                    <span className="td-tag-pill">
                      <IconMapPin /> {tournament.map_name}
                    </span>
                  )}
                  <span className="td-tag-pill">
                    <IconTarget /> {tournament.region || 'India'}
                  </span>
                </div>
              </div>

              {/* Sponsors strip */}
              {tournament.sponsors && tournament.sponsors.length > 0 && (
                <div className="td-hero-sponsors">
                  <span className="td-hero-sponsors-label">✦ Sponsored by</span>
                  <div className="td-hero-sponsors-divider" />
                  <div className="td-hero-sponsors-items">
                    {tournament.sponsors.map((sp) => (
                      <a
                        key={sp.id}
                        href={sp.website_url || undefined}
                        target={sp.website_url ? '_blank' : undefined}
                        rel="noopener noreferrer"
                        className="td-hero-sponsor-chip"
                        style={{ cursor: sp.website_url ? 'pointer' : 'default' }}
                      >
                        {sp.logo ? (
                          <img src={sp.logo} alt={sp.name} className="td-hero-sponsor-chip-logo" />
                        ) : (
                          <div className="td-hero-sponsor-chip-initial">
                            {sp.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="td-hero-sponsor-chip-name">{sp.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: stat boxes + watch live */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="td-stat-boxes">
                <div className="td-stat-box">
                  <div className="td-stat-label">Entry Fee</div>
                  <div className="td-stat-value">{entryFeeNum}</div>
                </div>
                <div className="td-stat-box">
                  <div className="td-stat-label">Capacity</div>
                  <div className="td-stat-value">
                    <span className="td-stat-value-accent">{tournament.current_participants}</span>
                    <span className="td-stat-value-muted">/{tournament.max_participants}</span>
                  </div>
                </div>
              </div>

              {tournament.live_link && (
                <a
                  href={tournament.live_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="td-watch-live-btn"
                >
                  <div className="td-watch-live-pulse" />
                  <IconVideo className="td-watch-live-icon" />
                  <span className="td-watch-live-label">Watch Live</span>
                  <IconExternalLink />
                </a>
              )}

              {/* Mobile-only: registration + share shown after stat boxes */}
              <div className="td-hero-mobile-actions">
                {renderRegistrationButton()}
                <div className="td-action-tags">
                  <button className="td-share-btn" onClick={() => setShowShareModal(true)}>
                    <IconShare /> Share
                  </button>
                  {tournament.map_name && (
                    <span className="td-tag-pill">
                      <IconMapPin /> {tournament.map_name}
                    </span>
                  )}
                  <span className="td-tag-pill">
                    <IconTarget /> {tournament.region || 'India'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CONTENT SECTION (dark bg)
      ══════════════════════════════════════ */}
      <div style={{ background: 'hsl(220 10% 6%)', paddingTop: 28, paddingBottom: 48 }}>
        {/* ══════════════════════════════════════
          TAB SECTION
      ══════════════════════════════════════ */}
        <div className="td-tabs-section">
          {/* Tab navigation */}
          <div className="td-tab-list" role="tablist">
            <button
              role="tab"
              className={`td-tab-btn${activeTab === 'schedule' ? ' active' : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              <IconClock />
              <span className="td-tab-label-full">Schedule &amp; Prizes</span>
              <span className="td-tab-label-short">Schedule</span>
            </button>
            <button
              role="tab"
              className={`td-tab-btn${activeTab === 'calendar' ? ' active' : ''}`}
              onClick={() => setActiveTab('calendar')}
            >
              📅 Calendar
            </button>
            <button
              role="tab"
              className={`td-tab-btn${activeTab === 'roadmap' ? ' active' : ''}`}
              onClick={() => setActiveTab('roadmap')}
            >
              🗺 Roadmap
            </button>
            <button
              role="tab"
              className={`td-tab-btn${activeTab === 'briefing' ? ' active' : ''}`}
              onClick={() => setActiveTab('briefing')}
            >
              <IconShield />
              <span className="td-tab-label-full">Briefing &amp; Rules</span>
              <span className="td-tab-label-short">Briefing</span>
            </button>
            <button
              role="tab"
              className={`td-tab-btn${activeTab === 'host' ? ' active' : ''}`}
              onClick={() => setActiveTab('host')}
            >
              <IconUsers />
              <span className="td-tab-label-full">Host Details</span>
              <span className="td-tab-label-short">Host</span>
            </button>
            {tournament.sponsors && tournament.sponsors.length > 0 && (
              <button
                role="tab"
                className={`td-tab-btn${activeTab === 'sponsors' ? ' active' : ''}`}
                onClick={() => setActiveTab('sponsors')}
              >
                ✦ Sponsors
              </button>
            )}
          </div>

          {/* ── Tab: Schedule & Prizes ── */}
          {activeTab === 'schedule' && (
            <div className="td-tab-panel" role="tabpanel">
              <div className="td-two-col">
                {/* Left column: timeline + rounds + map pool */}
                <div>
                  {/* Timeline */}
                  <p className="td-section-heading">Timeline</p>
                  <ul className="td-timeline">
                    <li className="td-timeline-item">
                      <div className="td-timeline-dot td-timeline-dot-green" />
                      <div>
                        <div className="td-timeline-label td-timeline-label-green">
                          Registration Starts
                        </div>
                        <div className="td-timeline-value">
                          {fmtDate(tournament.registration_start)}
                        </div>
                      </div>
                    </li>
                    <li className="td-timeline-item">
                      <div className="td-timeline-dot td-timeline-dot-blue" />
                      <div>
                        <div className="td-timeline-label td-timeline-label-blue">
                          Registration Closes
                        </div>
                        <div className="td-timeline-value">
                          {fmtDate(tournament.registration_end)}
                        </div>
                      </div>
                    </li>
                    <li className="td-timeline-item">
                      <div className="td-timeline-dot td-timeline-dot-red" />
                      <div>
                        <div className="td-timeline-label td-timeline-label-red">Match Starts</div>
                        <div className="td-timeline-value">
                          {fmtDate(tournament.tournament_start)}
                        </div>
                      </div>
                    </li>
                  </ul>

                  {/* Rounds */}
                  {tournament.rounds && tournament.rounds.length > 0 && (
                    <>
                      <hr className="td-divider" />
                      <p className="td-section-heading">Rounds</p>
                      {tournament.rounds.map((round) => {
                        const matchCount = round.max_matches || tournament.max_matches;
                        const rd = tournament.round_dates?.[String(round.round)] || {};
                        const formatShortDate = (d) => {
                          if (!d) return '';
                          const dt = new Date(d + 'T00:00:00');
                          return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        };
                        const startStr = formatShortDate(rd.start_date);
                        const endStr = formatShortDate(rd.end_date);
                        let dateLabel = '';
                        if (startStr && endStr && startStr !== endStr) {
                          dateLabel = `${startStr}–${endStr}`;
                        } else if (startStr) {
                          dateLabel = startStr;
                        }
                        const modeLabel = rd.mode ? rd.mode.toUpperCase() : '';
                        const modeColor = rd.mode === 'online' ? '#10b981' : '#f97316'; // green for online, orange for offline
                        return (
                          <div key={round.round} className="td-round-item">
                            <span className="td-round-badge">
                              {round.round_name || `Round ${round.round}`}
                            </span>
                            <span className="td-round-meta">
                              {dateLabel && <span className="td-round-date">{dateLabel}</span>}
                              {modeLabel && (
                                <span
                                  style={{
                                    background: `${modeColor}18`,
                                    border: `1.5px solid ${modeColor}`,
                                    color: modeColor,
                                    padding: '2px 10px',
                                    borderRadius: '20px',
                                    fontSize: '10px',
                                    fontWeight: '800',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {modeLabel}
                                </span>
                              )}
                              {matchCount && (
                                <span className="td-round-matches">{`${matchCount} match${matchCount > 1 ? 'es' : ''}`}</span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Map Pool */}
                  <hr className="td-divider" />
                  <p className="td-section-heading">Map Pool</p>
                  {(() => {
                    // Use real match_maps from backend if available, else fall back to game defaults
                    const matchMapsRaw = tournament.match_maps;
                    const maxMatches = tournament.max_matches || 4;
                    const hasRealMaps =
                      matchMapsRaw &&
                      typeof matchMapsRaw === 'object' &&
                      Object.keys(matchMapsRaw).length > 0;

                    let mapsToShow = [];
                    if (hasRealMaps) {
                      // matchMaps is {1: 'Erangel', 2: 'Miramar', ...} — get unique map names
                      mapsToShow = [...new Set(Object.values(matchMapsRaw))];
                    } else {
                      mapsToShow = getGameMaps(tournament.game_name);
                    }

                    const mapInfo = getGameMapInfo(tournament.game_name);

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid hsl(220 5% 25%)',
                            paddingBottom: 12,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 14, fontWeight: '600', color: 'white' }}>
                              {tournament.game_name}
                            </span>
                            {!hasRealMaps && mapInfo.boInfo && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: 'hsl(220 5% 60%)',
                                  padding: '2px 6px',
                                  backgroundColor: 'hsl(220 5% 20%)',
                                  borderRadius: '4px',
                                }}
                              >
                                {mapInfo.boInfo}
                              </span>
                            )}
                          </div>
                          <span
                            style={{ fontSize: 12, color: 'hsl(220 5% 60%)', fontWeight: '500' }}
                          >
                            {maxMatches} match{maxMatches !== 1 ? 'es' : ''}
                          </span>
                        </div>
                        {mapsToShow.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {hasRealMaps ? (
                              // Show per-match map assignments
                              Object.entries(matchMapsRaw)
                                .sort(([a], [b]) => Number(a) - Number(b))
                                .map(([matchNum, mapName]) => (
                                  <div
                                    key={matchNum}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '6px 10px',
                                      background: 'hsl(220 5% 14%)',
                                      borderRadius: 8,
                                      border: '1px solid hsl(220 5% 22%)',
                                    }}
                                  >
                                    <span style={{ fontSize: 12, color: 'hsl(220 5% 55%)' }}>
                                      Match {matchNum}
                                    </span>
                                    <span className="td-map-badge">
                                      <IconMapPin /> {mapName}
                                    </span>
                                  </div>
                                ))
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {mapsToShow.map((map) => (
                                  <span key={map} className="td-map-badge">
                                    <IconMapPin /> {map}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ color: 'hsl(220 5% 55%)', fontSize: 13 }}>
                            Map pool not specified for {tournament.game_name}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Right column: prize distribution */}
                {(() => {
                  // Build prize tiers from prize_distribution
                  let prizeData = [];
                  if (
                    tournament.prize_distribution &&
                    typeof tournament.prize_distribution === 'object' &&
                    Object.keys(tournament.prize_distribution).length > 0
                  ) {
                    prizeData = Object.entries(tournament.prize_distribution).map(
                      ([place, amount]) => ({ place, amount: parseInt(amount) || 0 })
                    );
                  } else {
                    prizeData = [
                      { place: '1st', amount: prizePool * 0.5 },
                      { place: '2nd', amount: prizePool * 0.3 },
                      { place: '3rd', amount: prizePool * 0.2 },
                    ];
                  }

                  // Build rank → team name map (only for completed tournaments)
                  const rankMap = {};
                  if (tournament.status === 'completed') {
                    tournamentResults.forEach((entry) => {
                      rankMap[entry.rank] = entry.team_name;
                    });
                    if (Object.keys(rankMap).length === 0 && tournament.winner_name) {
                      rankMap[1] = tournament.winner_name;
                    }
                  }

                  const isCompleted = tournament.status === 'completed';
                  const medals = ['🥇', '🥈', '🥉'];
                  const placeColors = {
                    '1st': {
                      row: 'td-prize-row-1',
                      label: 'td-prize-label-1',
                      amount: 'td-prize-amount-1',
                    },
                    '2nd': {
                      row: 'td-prize-row-2',
                      label: 'td-prize-label-2',
                      amount: 'td-prize-amount-2',
                    },
                    '3rd': {
                      row: 'td-prize-row-3',
                      label: 'td-prize-label-3',
                      amount: 'td-prize-amount-3',
                    },
                  };
                  const defaultColors = {
                    row: 'td-prize-row-2',
                    label: 'td-prize-label-2',
                    amount: 'td-prize-amount-2',
                  };

                  // Coupon data
                  const cd = tournament.coupon_distribution || {};
                  const hasCouponData =
                    Array.isArray(cd.tiers) &&
                    cd.tiers.length > 0 &&
                    cd.tiers.some((t) => t.coupons?.length > 0);
                  const hasSpecialAwards =
                    Array.isArray(tournament.special_awards) &&
                    tournament.special_awards.length > 0;

                  // Collect unique sponsor names for "Powered by" header
                  const allCouponSponsors = [];
                  if (hasCouponData) {
                    [...(cd.tiers || []), ...(cd.special_awards || [])].forEach((tier) => {
                      (tier.coupons || []).forEach((c) => {
                        if (c.name && !allCouponSponsors.find((s) => s.name === c.name)) {
                          allCouponSponsors.push({ name: c.name, link: c.link });
                        }
                      });
                    });
                  }

                  const couponMedals = ['🥇', '🥈', '🥉'];

                  return (
                    <div>
                      <p className="td-section-heading">Prize Distribution</p>

                      {/* Tab switcher — only shown when coupon data exists */}
                      {hasCouponData && (
                        <div className="td-prize-tab-switcher">
                          <button
                            className={`td-prize-tab-btn${prizeTab === 'cash' ? ' active' : ''}`}
                            onClick={() => setPrizeTab('cash')}
                          >
                            💰 Cash (INR)
                          </button>
                          <button
                            className={`td-prize-tab-btn${prizeTab === 'coupons' ? ' active' : ''}`}
                            onClick={() => setPrizeTab('coupons')}
                          >
                            <IconTicket /> Coupons
                          </button>
                        </div>
                      )}

                      {/* ── Cash tab (default) ── */}
                      {(!hasCouponData || prizeTab === 'cash') && (
                        <>
                          {prizeData.map((prize, idx) => {
                            const colors = placeColors[prize.place] || defaultColors;
                            const teamName = rankMap[idx + 1];
                            const medal = medals[idx];
                            const tagColors = ['#f9c22a', '#9ca3af', '#f97316'];
                            const tagColor = tagColors[idx] || '#9ca3af';
                            return (
                              <div key={prize.place} className={`td-prize-row ${colors.row}`}>
                                <div className={`td-prize-label ${colors.label}`}>
                                  {isCompleted && medal ? (
                                    <span style={{ fontSize: 16 }}>{medal}</span>
                                  ) : (
                                    <IconTrophy style={{ width: 18, height: 18 }} />
                                  )}
                                  <span>{prize.place} Place</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {isCompleted && teamName && (
                                    <span
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: tagColor,
                                        background: `${tagColor}22`,
                                        border: `1px solid ${tagColor}60`,
                                        borderRadius: 20,
                                        padding: '2px 10px',
                                        letterSpacing: '0.02em',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {teamName}
                                    </span>
                                  )}
                                  <span className={`td-prize-amount ${colors.amount}`}>
                                    {formatPrize(prize.amount)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          <p className="td-prize-total">
                            Total Cash Pool:{' '}
                            <strong style={{ color: '#f9c22a' }}>₹{tournament.prize_pool}</strong>
                          </p>

                          {/* Special Awards (cash) */}
                          {hasSpecialAwards && (
                            <div className="td-special-awards">
                              <p className="td-special-awards-heading">
                                <IconStar /> Special Awards
                              </p>
                              <div className="td-special-awards-grid">
                                {tournament.special_awards.map((award, idx) => (
                                  <div key={idx} className="td-special-award-row">
                                    <span className="td-special-award-name">{award.name}</span>
                                    <span className="td-special-award-amount">
                                      {formatPrize(parseInt(award.amount) || 0)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* ── Coupons tab ── */}
                      {hasCouponData &&
                        prizeTab === 'coupons' &&
                        (() => {
                          const ensureUrl = (link) => {
                            if (!link) return undefined;
                            return /^https?:\/\//i.test(link) ? link : `https://${link}`;
                          };
                          // Index-based sponsor brand colors (cycles for 3+ sponsors)
                          const CHIP_COLORS = [
                            {
                              bg: 'rgba(20,83,45,0.5)',
                              border: 'rgba(34,197,94,0.28)',
                              label: '#4ade80',
                            },
                            {
                              bg: 'rgba(88,28,135,0.45)',
                              border: 'rgba(139,92,246,0.38)',
                              label: '#a78bfa',
                            },
                            {
                              bg: 'rgba(30,58,138,0.45)',
                              border: 'rgba(96,165,250,0.32)',
                              label: '#60a5fa',
                            },
                            {
                              bg: 'rgba(120,53,15,0.45)',
                              border: 'rgba(251,146,60,0.32)',
                              label: '#fb923c',
                            },
                          ];
                          const tierRings = [
                            'rgba(234,179,8,0.4)',
                            'rgba(180,180,180,0.2)',
                            'rgba(249,115,22,0.3)',
                          ];

                          // Group same-rank tiers
                          const grouped = [];
                          (cd.tiers || []).forEach((tier) => {
                            const existing = grouped.find((g) => g.rank === tier.rank);
                            if (existing) existing.coupons.push(...(tier.coupons || []));
                            else
                              grouped.push({ rank: tier.rank, coupons: [...(tier.coupons || [])] });
                          });

                          const renderChips = (coupons) =>
                            (coupons || []).map((coupon, cIdx) => {
                              const url = ensureUrl(coupon.link);
                              const chip = CHIP_COLORS[cIdx % CHIP_COLORS.length];
                              return (
                                <a
                                  key={cIdx}
                                  href={url}
                                  target={url ? '_blank' : undefined}
                                  rel="noopener noreferrer"
                                  className={`td-coupon-amount-chip${url ? ' clickable' : ''}`}
                                  style={{ background: chip.bg, borderColor: chip.border }}
                                >
                                  <span
                                    className="td-coupon-chip-sponsor"
                                    style={{ color: chip.label }}
                                  >
                                    {coupon.name}
                                  </span>
                                  <span className="td-coupon-chip-amount">
                                    {Number(coupon.amount).toLocaleString('en-IN')} coupons
                                  </span>
                                </a>
                              );
                            });

                          return (
                            <>
                              {/* Powered by header */}
                              {allCouponSponsors.length > 0 && (
                                <div className="td-coupon-powered-by">
                                  <span>Powered by</span>
                                  {allCouponSponsors.map((sp, idx) => (
                                    <span key={sp.name}>
                                      {idx > 0 && (
                                        <span className="td-coupon-powered-sep"> + </span>
                                      )}
                                      {sp.link ? (
                                        <a
                                          href={ensureUrl(sp.link)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="td-coupon-sponsor-link"
                                        >
                                          {sp.name} ↗
                                        </a>
                                      ) : (
                                        <span className="td-coupon-sponsor-link">{sp.name}</span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Placement tier cards */}
                              {grouped.map((tier, idx) => (
                                <div
                                  key={tier.rank}
                                  className="td-coupon-tier-card"
                                  style={{
                                    boxShadow: `0 0 0 1px ${tierRings[idx] || 'rgba(255,255,255,0.08)'}`,
                                  }}
                                >
                                  <div className="td-coupon-tier-header">
                                    <span>{couponMedals[idx] || '🏆'}</span>
                                    <span className="td-coupon-tier-rank">{tier.rank} Place</span>
                                  </div>
                                  <div className="td-coupon-tier-chips">
                                    {renderChips(tier.coupons)}
                                  </div>
                                </div>
                              ))}

                              <p className="td-coupon-footer">
                                Tap a coupon to visit the partner site and redeem.
                              </p>

                              {/* Coupon special awards */}
                              {(cd.special_awards || []).length > 0 && (
                                <div className="td-special-awards">
                                  <p className="td-special-awards-heading">
                                    <IconStar /> Special Awards
                                  </p>
                                  {(cd.special_awards || []).map((award, aIdx) => (
                                    <div
                                      key={aIdx}
                                      className="td-coupon-tier-card"
                                      style={{ boxShadow: '0 0 0 1px rgba(234,179,8,0.25)' }}
                                    >
                                      <div className="td-coupon-tier-header">
                                        <span style={{ fontSize: 14 }}>⭐</span>
                                        <span className="td-coupon-tier-rank">{award.name}</span>
                                      </div>
                                      <div className="td-coupon-tier-chips">
                                        {renderChips(award.coupons)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── Tab: Briefing & Rules ── */}
          {activeTab === 'briefing' && (
            <div className="td-tab-panel" role="tabpanel">
              <div className="td-briefing-col">
                {/* Left: description + rules */}
                <div>
                  <p className="td-section-heading">About This Tournament</p>
                  <p className="td-description">
                    {tournament.description || 'No description provided.'}
                  </p>

                  {tournament.rules && (
                    <>
                      <hr className="td-divider" />
                      <p className="td-section-heading">Rules &amp; Guidelines</p>
                      {typeof tournament.rules === 'string' ? (
                        <div className="td-rules-grid">
                          {tournament.rules
                            .split('\n')
                            .filter((r) => r.trim())
                            .map((rule, i) => (
                              <div key={i} className="td-rule-item">
                                <div className="td-rule-dot" />
                                <span className="td-rule-text">{rule.trim()}</span>
                              </div>
                            ))}
                        </div>
                      ) : Array.isArray(tournament.rules) ? (
                        <div className="td-rules-grid">
                          {tournament.rules.map((rule, i) => (
                            <div key={i} className="td-rule-item">
                              <div className="td-rule-dot" />
                              <span className="td-rule-text">{rule}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                {/* Right: Placement Points */}
                <div>
                  {tournament.placement_points &&
                  typeof tournament.placement_points === 'object' &&
                  Object.keys(tournament.placement_points).length > 0 ? (
                    <>
                      <p
                        className="td-section-heading"
                        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <span style={{ color: 'hsl(270 60% 65%)', fontSize: 16 }}>⊙</span>
                        Placement Points
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {Object.entries(tournament.placement_points)
                          .sort(([a], [b]) => Number(a) - Number(b))
                          .map(([rank, pts]) => {
                            const isTop3 = Number(rank) <= 3;
                            const isKill = isNaN(Number(rank));
                            return (
                              <div
                                key={rank}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '8px 14px',
                                  background: 'hsl(220 5% 12%)',
                                  borderRadius: 8,
                                  border: '1px solid hsl(220 5% 20%)',
                                }}
                              >
                                <span style={{ fontSize: 13, color: 'hsl(220 5% 75%)' }}>
                                  {isKill
                                    ? rank
                                    : `${rank}${rank === '1' ? 'st' : rank === '2' ? 'nd' : rank === '3' ? 'rd' : 'th'} Place`}
                                </span>
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    padding: '2px 10px',
                                    borderRadius: 20,
                                    background:
                                      isTop3 || isKill ? 'hsl(270 60% 55% / 0.2)' : 'transparent',
                                    color:
                                      isTop3 || isKill ? 'hsl(270 60% 75%)' : 'hsl(220 5% 65%)',
                                    border:
                                      isTop3 || isKill
                                        ? '1px solid hsl(270 60% 55% / 0.4)'
                                        : 'none',
                                  }}
                                >
                                  {isKill ? `+${pts} pt` : `${pts} pts`}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Host Details ── */}
          {activeTab === 'host' && (
            <div className="td-tab-panel" role="tabpanel">
              {/* Host card */}
              <Link
                to={`/host/profile/${tournament.host?.id}`}
                className="td-host-card"
                style={{ display: 'flex' }}
              >
                <div className="td-host-avatar">
                  {(tournament.host?.user?.username || 'H').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="td-host-name">
                      {tournament.host?.user?.username || 'Tournament Host'}
                    </span>
                    {tournament.host?.verified && <IconBadgeCheck />}
                  </div>
                  {tournament.host?.verified && (
                    <div className="td-host-verified">Verified by Scrimverse</div>
                  )}
                  <div className="td-host-stats">
                    <span className="td-host-stat-tag purple">
                      <IconTrophy style={{ width: 11, height: 11 }} />
                      {tournament.host?.total_tournaments_hosted ?? 0} tournaments
                    </span>
                    {tournament.host?.average_rating > 0 && (
                      <span className="td-host-stat-tag green">
                        <IconStar />
                        {Number(tournament.host.average_rating).toFixed(1)} rating
                      </span>
                    )}
                  </div>
                </div>
                <div className="td-host-arrow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ width: 18, height: 18, color: 'hsl(220 5% 50%)' }}
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              </Link>

              {/* Host stats grid */}
              <div className="td-host-stats-grid">
                <div className="td-host-stats-box">
                  <div className="td-host-stats-number blue">
                    {tournament.host?.total_tournaments_hosted ?? 0}
                  </div>
                  <div className="td-host-stats-label">Tournaments</div>
                </div>
                <div className="td-host-stats-box">
                  <div className="td-host-stats-number yellow">
                    {tournament.host?.average_rating > 0
                      ? Number(tournament.host.average_rating).toFixed(1)
                      : '—'}
                  </div>
                  <div className="td-host-stats-label">Rating</div>
                </div>
              </div>

              {/* Trusted banner */}
              <div className="td-trusted-banner">
                <div className="td-trusted-banner-title">
                  <IconShield />
                  Verified by Scrimverse • Trusted Host
                </div>
                <p className="td-trusted-banner-desc">
                  This organizer has been verified and has successfully hosted multiple events.
                </p>
              </div>

              {/* Bio */}
              {tournament.host?.bio && (
                <>
                  <hr className="td-divider" />
                  <p className="td-section-heading">About</p>
                  <p className="td-description">{tournament.host.bio}</p>
                </>
              )}

              {/* Contact */}
              {tournament.host?.contact_email && (
                <>
                  <hr className="td-divider" />
                  <p className="td-section-heading">Contact</p>
                  <a
                    href={`mailto:${tournament.host.contact_email}`}
                    style={{ color: 'hsl(200 85% 60%)', fontSize: 14, fontWeight: 600 }}
                  >
                    {tournament.host.contact_email}
                  </a>
                </>
              )}

              {/* Extra links */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                {tournament.tournament_file && (
                  <a
                    href={tournament.tournament_file}
                    download
                    style={{
                      display: 'block',
                      padding: '12px 16px',
                      borderRadius: 10,
                      background: 'hsl(220 10% 14%)',
                      border: '1px solid hsl(200 85% 60% / 0.3)',
                      color: 'hsl(200 85% 60%)',
                      fontWeight: 600,
                      fontSize: 14,
                      textAlign: 'center',
                    }}
                  >
                    📄 Download Rules PDF
                  </a>
                )}
                {(tournament.status === 'completed' || tournament.round_scores?.length > 0) && (
                  <Link
                    to={`/tournaments/${id}/stats`}
                    style={{
                      display: 'block',
                      padding: '12px 16px',
                      borderRadius: 10,
                      background: 'hsl(270 60% 55%)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      textAlign: 'center',
                    }}
                  >
                    📊 View Tournament Stats
                  </Link>
                )}
                {tournament.discord_id && (
                  <a
                    href={`https://${tournament.discord_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      padding: '12px 16px',
                      borderRadius: 10,
                      background: 'hsl(220 10% 14%)',
                      border: '1px solid hsl(270 60% 55% / 0.3)',
                      color: 'hsl(270 60% 65%)',
                      fontWeight: 600,
                      fontSize: 14,
                      textAlign: 'center',
                    }}
                  >
                    💬 Join Discord Server
                  </a>
                )}
              </div>
            </div>
          )}

          {/* ── Tab: Calendar ── */}
          {activeTab === 'calendar' && (
            <div className="td-tab-panel" role="tabpanel">
              {(() => {
                // Parse a date string in LOCAL time (avoids UTC-shift bug in IST)
                const parseLocal = (s) => {
                  if (!s) return null;
                  const str = typeof s === 'string' ? s : s.toISOString().slice(0, 10);
                  return /^\d{4}-\d{2}-\d{2}$/.test(str)
                    ? new Date(str + 'T00:00:00')
                    : new Date(str);
                };

                const regStart = tournament.registration_start
                  ? parseLocal(tournament.registration_start)
                  : null;
                const regEnd = tournament.registration_end
                  ? parseLocal(tournament.registration_end)
                  : null;
                const roundDates = tournament.round_dates || {};
                const roundNames = tournament.round_names || {};

                // Color palette per round index (0-based) — matches reference stage colors
                const ROUND_COLORS = [
                  '#8b5cf6', // Round 1 — purple-500 (Qualifiers)
                  '#6d28d9', // Round 2 — purple-700 (QF)
                  '#fde047', // Round 3 — yellow-300 (Semi Finals)
                  '#f59e0b', // Round 4 — amber-400 (Finals)
                ];
                // Format a Date as YYYY-MM-DD using local components (not UTC)
                const toLocalISO = (d) =>
                  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

                // Build date → { label, color } map
                const dateMap = {};
                const eachDay = (start, end) => {
                  const days = [];
                  const cur = parseLocal(typeof start === 'string' ? start : toLocalISO(start));
                  cur.setHours(0, 0, 0, 0);
                  const endD = parseLocal(typeof end === 'string' ? end : toLocalISO(end));
                  endD.setHours(0, 0, 0, 0);
                  while (cur <= endD) {
                    days.push(toLocalISO(cur));
                    cur.setDate(cur.getDate() + 1);
                  }
                  return days;
                };

                // Registration period
                if (regStart && regEnd) {
                  eachDay(regStart, regEnd).forEach((d) => {
                    dateMap[d] = { label: 'Reg Open', color: '#10b981' };
                  });
                }

                // Each round
                const roundNums = Object.keys(roundDates)
                  .map(Number)
                  .sort((a, b) => a - b);
                roundNums.forEach((rNum, idx) => {
                  const rd = roundDates[String(rNum)];
                  if (!rd?.start_date || !rd?.end_date) return;
                  const isLast = rNum === Math.max(...roundNums);
                  const color = isLast
                    ? '#d97706'
                    : ROUND_COLORS[Math.min(idx, ROUND_COLORS.length - 1)];
                  const label = roundNames[String(rNum)] || `Round ${rNum}`;
                  eachDay(rd.start_date, rd.end_date).forEach((d) => {
                    dateMap[d] = { label, color };
                  });
                });

                // Rest days: days between rounds that are in the tournament span but have no event
                if (roundNums.length > 0 && regStart) {
                  const lastRound = roundDates[String(Math.max(...roundNums))];
                  const spanEnd = lastRound?.end_date ? parseLocal(lastRound.end_date) : regEnd;
                  if (spanEnd) {
                    eachDay(regStart, spanEnd).forEach((d) => {
                      if (!dateMap[d]) {
                        dateMap[d] = { label: 'Rest', color: '#6b7280' };
                      }
                    });
                  }
                }

                // Determine which months to render
                const allDates = Object.keys(dateMap).sort();
                if (allDates.length === 0) {
                  return (
                    <p style={{ color: 'hsl(220 5% 55%)', fontSize: 13 }}>
                      No tournament dates configured yet.
                    </p>
                  );
                }

                const firstDate = new Date(allDates[0]);
                const lastDate = new Date(allDates[allDates.length - 1]);
                firstDate.setDate(1);
                lastDate.setDate(1);

                const months = [];
                const cur = new Date(firstDate);
                while (cur <= lastDate) {
                  months.push({ year: cur.getFullYear(), month: cur.getMonth() });
                  cur.setMonth(cur.getMonth() + 1);
                }
                // Always show at least 2 months side by side
                const targetMonths = Math.max(months.length, 2);
                while (months.length < targetMonths) {
                  const last = months[months.length - 1];
                  const next = new Date(last.year, last.month + 1, 1);
                  months.push({ year: next.getFullYear(), month: next.getMonth() });
                }

                const MONTH_NAMES = [
                  'January',
                  'February',
                  'March',
                  'April',
                  'May',
                  'June',
                  'July',
                  'August',
                  'September',
                  'October',
                  'November',
                  'December',
                ];
                const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

                // Build legend from unique events present
                const seenLabels = new Set();
                const legendItems = [];
                Object.values(dateMap).forEach(({ label, color }) => {
                  if (!seenLabels.has(label)) {
                    seenLabels.add(label);
                    legendItems.push({ label, color });
                  }
                });

                return (
                  <div className="td-calendar-outer">
                    <div className="td-roadmap-outer-header">
                      <IconCalendar />
                      <span className="td-roadmap-outer-title">Tournament Calendar</span>
                    </div>
                    <div className="td-calendar-months">
                      {months.map(({ year, month }) => {
                        const firstDay = new Date(year, month, 1).getDay();
                        const daysInMonth = new Date(year, month + 1, 0).getDate();
                        const cells = Array(firstDay).fill(null);
                        for (let d = 1; d <= daysInMonth; d++) cells.push(d);

                        return (
                          <div key={`${year}-${month}`} className="td-calendar-month">
                            <p className="td-calendar-month-title">
                              {MONTH_NAMES[month]} {year}
                            </p>
                            <div className="td-calendar-grid">
                              {DOW.map((d, i) => (
                                <span key={i} className="td-calendar-dow">
                                  {d}
                                </span>
                              ))}
                              {cells.map((day, i) => {
                                if (!day)
                                  return <div key={`e-${i}`} className="td-calendar-cell-empty" />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const evt = dateMap[dateStr];
                                return (
                                  <div
                                    key={dateStr}
                                    className={`td-calendar-cell${evt ? ' td-calendar-cell-event' : ''}`}
                                    title={evt ? evt.label : undefined}
                                    style={
                                      evt
                                        ? {
                                            background: evt.color + '40',
                                            borderColor: evt.color + 'aa',
                                          }
                                        : {}
                                    }
                                  >
                                    <span className="td-calendar-day-num">{day}</span>
                                    {evt && (
                                      <span
                                        className="td-calendar-day-label"
                                        style={{ color: evt.color }}
                                      >
                                        {evt.label}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="td-calendar-legend">
                      {legendItems.map(({ label, color }) => (
                        <span key={label} className="td-calendar-legend-item">
                          <span className="td-calendar-legend-dot" style={{ background: color }} />
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Tab: Roadmap ── */}
          {activeTab === 'roadmap' && (
            <div className="td-tab-panel" role="tabpanel">
              {(() => {
                const rounds = Array.isArray(tournament.rounds) ? tournament.rounds : [];
                const roundNames = tournament.round_names || {};

                if (rounds.length === 0) {
                  return (
                    <p style={{ color: 'hsl(220 5% 55%)', fontSize: 13 }}>
                      No round structure configured yet.
                    </p>
                  );
                }

                const stages = rounds.map((r, idx) => {
                  const stageName = roundNames[String(r.round)] || `Round ${r.round}`;
                  const nextName = roundNames[String(r.round + 1)];
                  const isLast = idx === rounds.length - 1;
                  const advancement = isLast
                    ? `Top ${r.qualifying_teams || r.max_teams || 1} earn prize points & trophy`
                    : `Top ${r.qualifying_teams || r.max_teams || '?'} advance to ${nextName || 'next stage'}`;
                  return { stageNum: r.round, stageName, teams: r.max_teams, advancement, isLast };
                });

                return (
                  <div className="td-roadmap-outer">
                    <div className="td-roadmap-outer-header">
                      <IconTrophy style={{ width: 15, height: 15, color: '#eab308' }} />
                      <span className="td-roadmap-outer-title">Tournament Roadmap</span>
                    </div>
                    <div className="td-roadmap-flow">
                      {stages.map((stage, idx) => (
                        <div key={stage.stageNum} className="td-roadmap-stage-wrap">
                          <div
                            className={`td-roadmap-card${stage.isLast ? ' td-roadmap-card-final' : ''}`}
                          >
                            <div className="td-roadmap-card-inner">
                              <div>
                                <div className="td-roadmap-card-header">
                                  <span className="td-roadmap-stage-label">
                                    Stage {stage.stageNum}
                                  </span>
                                  {stage.teams > 0 && (
                                    <span className="td-roadmap-teams-badge">
                                      {stage.teams} Teams
                                    </span>
                                  )}
                                </div>
                                <p className="td-roadmap-name">{stage.stageName}</p>
                              </div>
                              <p className="td-roadmap-advancement">{stage.advancement}</p>
                            </div>
                          </div>
                          {idx < stages.length - 1 && (
                            <IconChevronRight
                              style={{
                                width: 18,
                                height: 18,
                                color: 'hsl(270 60% 50% / 0.5)',
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Tab: Sponsors ── */}
          {activeTab === 'sponsors' && (
            <div className="td-tab-panel" role="tabpanel">
              <p className="td-section-heading" style={{ marginBottom: 20 }}>
                Our Sponsors
              </p>
              {(() => {
                const groups = {};
                (tournament.sponsors || []).forEach((sp) => {
                  const key = sp.sponsor_type || 'Official Sponsors';
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(sp);
                });
                return Object.entries(groups).map(([type, items]) => (
                  <div key={type} className="td-sponsor-group">
                    <div className="td-sponsor-group-label">{type}</div>
                    <div className="td-sponsor-cards">
                      {items.map((sp) => (
                        <a
                          key={sp.id}
                          href={sp.website_url || undefined}
                          target={sp.website_url ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          className={`td-sponsor-card${sp.website_url ? ' clickable' : ''}`}
                        >
                          {sp.logo ? (
                            <img src={sp.logo} alt={sp.name} className="td-sponsor-card-logo" />
                          ) : (
                            <div className="td-sponsor-card-initial">
                              {sp.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="td-sponsor-card-info">
                            <span className="td-sponsor-card-name">{sp.name}</span>
                            {sp.sponsor_type && (
                              <span className="td-sponsor-card-type">{sp.sponsor_type}</span>
                            )}
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
        {/* end td-tabs-section */}
      </div>

      {/* end content section */}

      {/* ══════════════════════════════════════
          MATCH SCHEDULE — hidden from tournament detail page
      ══════════════════════════════════════ */}
      {false && (matchSchedule.length > 0 || scheduleLoading) && (
        <div className="td-match-schedule">
          <div className="bg-dark-bg-primary rounded-2xl border border-dark-bg-hover overflow-hidden">
            {/* Header */}
            <button
              onClick={() => setExpandedSchedule(!expandedSchedule)}
              className="w-full flex items-center justify-between p-4 hover:bg-dark-bg-hover transition-colors"
            >
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-accent-purple"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Match Schedule
              </h3>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${expandedSchedule ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSchedule && Object.keys(scheduleByRound).length > 0 && (
              <div className="border-t border-dark-bg-hover">
                {/* Round Tabs */}
                <div className="flex gap-1 p-3 pb-0">
                  {Object.keys(scheduleByRound)
                    .map(Number)
                    .sort((a, b) => a - b)
                    .map((roundNum) => {
                      const roundName =
                        tournament?.round_names?.[String(roundNum)] || `Round ${roundNum}`;
                      const isActive = selectedScheduleRound === roundNum;
                      return (
                        <button
                          key={roundNum}
                          onClick={() => {
                            setSelectedScheduleRound(roundNum);
                            const groups = scheduleByRound[roundNum]?.groups || [];
                            if (groups.length > 0) {
                              setSelectedScheduleGroup(groups[0].group_name);
                            }
                          }}
                          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                            isActive
                              ? 'bg-accent-purple text-white shadow-lg shadow-accent-purple/30'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          {roundName}
                        </button>
                      );
                    })}
                </div>

                {/* Group Switcher */}
                {selectedScheduleRound &&
                  scheduleByRound[selectedScheduleRound]?.groups?.length > 1 && (
                    <div className="flex gap-1 px-3 pt-3">
                      {scheduleByRound[selectedScheduleRound].groups.map((g) => {
                        const isActive = selectedScheduleGroup === g.group_name;
                        return (
                          <button
                            key={g.group_name}
                            onClick={() => setSelectedScheduleGroup(g.group_name)}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                              isActive
                                ? 'bg-white/10 text-white border border-white/20'
                                : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                            }`}
                          >
                            {g.group_name}
                          </button>
                        );
                      })}
                    </div>
                  )}

                {/* Match Cards */}
                <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto">
                  {(() => {
                    const currentRoundData = scheduleByRound[selectedScheduleRound];
                    if (!currentRoundData) return null;
                    const currentGroup =
                      currentRoundData.groups.find((g) => g.group_name === selectedScheduleGroup) ||
                      currentRoundData.groups[0];
                    if (!currentGroup) return null;

                    return currentGroup.matches
                      .sort((a, b) => a.match_number - b.match_number)
                      .map((match) => {
                        const timeStr = match.scheduled_time
                          ? (() => {
                              const [h, m] = match.scheduled_time.split(':');
                              const d = new Date();
                              d.setHours(parseInt(h), parseInt(m));
                              return d.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                              });
                            })()
                          : null;
                        const dateStr = match.scheduled_date
                          ? new Date(match.scheduled_date + 'T00:00:00').toLocaleDateString(
                              'en-US',
                              { month: 'short', day: 'numeric' }
                            )
                          : null;

                        return (
                          <div
                            key={match.id}
                            className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                              match.status === 'ongoing'
                                ? 'bg-green-500/5 border-green-500/30'
                                : match.status === 'completed'
                                  ? 'bg-white/[0.02] border-white/5 opacity-60'
                                  : 'bg-white/[0.02] border-white/5 hover:border-white/10'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black border ${
                                  match.status === 'ongoing'
                                    ? 'bg-green-500/10 border-green-500/30 text-green-400'
                                    : match.status === 'completed'
                                      ? 'bg-white/5 border-white/10 text-gray-500'
                                      : 'bg-accent-purple/10 border-accent-purple/30 text-accent-purple'
                                }`}
                              >
                                M{match.match_number}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  {match.map_name && (
                                    <span className="text-gray-300 text-sm flex items-center gap-1">
                                      <svg
                                        className="w-3 h-3 text-gray-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                        />
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                      </svg>
                                      {match.map_name}
                                    </span>
                                  )}
                                  {match.status === 'ongoing' && (
                                    <span className="flex items-center gap-1 text-[9px] font-bold text-green-400 uppercase">
                                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                                      Live
                                    </span>
                                  )}
                                  {match.status === 'completed' && (
                                    <span className="text-[9px] font-bold text-gray-500 uppercase">
                                      Completed
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {timeStr ? (
                                <>
                                  <p className="text-accent-purple font-bold text-sm">{timeStr}</p>
                                  {dateStr && (
                                    <p className="text-gray-500 text-[11px]">{dateStr}</p>
                                  )}
                                </>
                              ) : dateStr ? (
                                <p className="text-gray-400 text-xs">{dateStr}</p>
                              ) : (
                                <p className="text-gray-600 text-xs">TBD</p>
                              )}
                            </div>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>
            )}

            {scheduleLoading && (
              <div className="p-6 text-center">
                <div className="td-spinner" style={{ margin: '0 auto 8px' }} />
                <p className="text-gray-500 text-xs">Loading match schedule…</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          REGISTRATION MODAL
      ══════════════════════════════════════ */}
      {showRegisterModal && tournament && (
        <RegistrationModal
          event={tournament}
          type="tournament"
          onClose={() => setShowRegisterModal(false)}
          onSuccess={() => {
            setShowRegisterModal(false);
            fetchTournament();
          }}
        />
      )}

      {/* Team Registration Modal */}
      {showTeamRegistration && tournament && (
        <RegistrationModal
          event={tournament}
          type="tournament"
          onClose={() => setShowTeamRegistration(false)}
          onSuccess={() => {
            setShowTeamRegistration(false);
            fetchTournament();
          }}
        />
      )}

      {/* ══════════════════════════════════════
          SHARE MODAL
      ══════════════════════════════════════ */}
      {showShareModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowShareModal(false)}
        >
          <div className="td-share-card w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-accent-blue"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
                <h3 className="td-share-title">Share Tournament</h3>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <p className="td-share-sub">
              Invite friends to <span className="text-white font-semibold">{tournament.title}</span>
            </p>

            <div className="td-share-input">
              <input type="text" readOnly value={window.location.href} className="" />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setCopiedLink(true);
                  setTimeout(() => setCopiedLink(false), 2000);
                }}
                className="text-gray-400 hover:text-accent-blue transition-colors p-1.5 shrink-0"
                title="Copy URL"
              >
                {copiedLink ? (
                  <svg
                    className="w-5 h-5 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`🎮 Join *${tournament.title}* on ScrimVerse!\n\n🏆 Prize Pool: ₹${tournament.prize_pool}\n💰 Entry: ${parseFloat(tournament.entry_fee) === 0 ? 'FREE' : `₹${tournament.entry_fee}`}\n\n${window.location.href}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-dark-bg-hover hover:bg-green-500/10 border border-dark-bg-hover hover:border-green-500/30 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                  <svg className="w-5 h-5 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <span className="text-xs text-gray-300 font-medium">WhatsApp</span>
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`🎮 Join ${tournament.title} on ScrimVerse! 🏆 Prize: ₹${tournament.prize_pool}`)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-dark-bg-hover hover:bg-blue-500/10 border border-dark-bg-hover hover:border-blue-500/30 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </div>
                <span className="text-xs text-gray-300 font-medium">Twitter / X</span>
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setCopiedLink(true);
                  setTimeout(() => setCopiedLink(false), 2000);
                }}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-dark-bg-hover hover:bg-accent-purple/10 border border-dark-bg-hover hover:border-accent-purple/30 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-accent-purple/20 flex items-center justify-center group-hover:bg-accent-purple/30 transition-colors">
                  {copiedLink ? (
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 text-accent-purple"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                  )}
                </div>
                <span className="text-xs text-gray-300 font-medium">
                  {copiedLink ? 'Copied!' : 'Copy Link'}
                </span>
              </button>
            </div>

            <div className="td-qr-section">
              <h4 className="td-qr-title">Scan QR Code</h4>
              <div className="td-qr-box">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=360x360&data=${encodeURIComponent(window.location.href)}&bgcolor=ffffff&color=000000&format=png`}
                  alt="Tournament QR Code"
                  className="w-44 h-44"
                />
              </div>
              <div className="td-qr-actions">
                <button onClick={downloadQr} className="">
                  <svg
                    className="w-4 h-4 inline-block mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5 5 5M12 5v12"
                    />
                  </svg>
                  Download QR
                </button>
                <button
                  onClick={async () => {
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: tournament.title,
                          url: window.location.href,
                        });
                      } catch (err) {
                        /* cancelled */
                      }
                    } else {
                      try {
                        await navigator.clipboard.writeText(window.location.href);
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                      } catch (e) {
                        /* ignore */
                      }
                    }
                  }}
                  className=""
                >
                  <svg
                    className="w-4 h-4 inline-block mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  {navigator.share ? 'Share' : copiedLink ? 'Copied' : 'Copy Link'}
                </button>
              </div>
              <p className="text-center text-gray-500 text-xs mt-3">
                Scan or download the QR to share
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentDetail;
